// Global Instructions Rule Applied!

import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Checkbox, Modal, Select, Space, Typography } from 'antd'
import type { ContactRelationshipTitleAdminRow } from '../../../state_manager/data_models/contact_relationships_config_model.ts'

const { Text } = Typography

interface ReplaceContactRelationshipTitleModalProps {
  open: boolean
  titles: ContactRelationshipTitleAdminRow[]
  protectedTitleIds: Set<string>
  initialSourceIds?: string[]
  confirmLoading?: boolean
  onConfirm: (sourceIds: string[], targetId: string, deleteSources: boolean) => void
  onCancel: () => void
}

// Replace (or merge) one-or-more contact relationship titles into a single
// target: every junction reference — company, deal and prospect contacts — is
// repointed atomically (rows whose target pairing already exists are trashed
// as duplicates). This is also the forced flow when deleting an in-use title.
// Sources and target must share a relationship type (the backend refuses
// cross-type replacement); this panel already scopes the modal to one type's
// titles. System-protected titles can be replaced INTO but never deleted, so
// choosing one as a source disables the delete checkbox.
export function ReplaceContactRelationshipTitleModal({
  open,
  titles,
  protectedTitleIds,
  initialSourceIds,
  confirmLoading,
  onConfirm,
  onCancel
}: ReplaceContactRelationshipTitleModalProps) {
  const [sourceIds, setSourceIds] = useState<string[]>([])
  const [targetId, setTargetId] = useState<string | undefined>(undefined)
  const [deleteSources, setDeleteSources] = useState(true)

  // Reset the form whenever the modal (re)opens.
  useEffect(() => {
    if (open) {
      setSourceIds(initialSourceIds ?? [])
      setTargetId(undefined)
      setDeleteSources(true)
    }
  }, [open, initialSourceIds])

  const sourceOptions = useMemo(
    () =>
      titles.map((t) => ({
        value: t.contact_relationship_titles__id,
        label: t.contact_relationship_titles__name
      })),
    [titles]
  )

  // The target cannot be a source.
  const targetOptions = useMemo(
    () =>
      titles
        .filter((t) => !sourceIds.includes(t.contact_relationship_titles__id))
        .map((t) => ({
          value: t.contact_relationship_titles__id,
          label: t.contact_relationship_titles__name
        })),
    [titles, sourceIds]
  )

  const hasProtectedSource = useMemo(
    () => sourceIds.some((id) => protectedTitleIds.has(id)),
    [sourceIds, protectedTitleIds]
  )

  useEffect(() => {
    if (hasProtectedSource && deleteSources) setDeleteSources(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProtectedSource])

  const affectedReferences = useMemo(
    () =>
      titles
        .filter((t) => sourceIds.includes(t.contact_relationship_titles__id))
        .reduce((sum, t) => sum + (t.contact_relationship_titles__usage_count || 0), 0),
    [titles, sourceIds]
  )

  const targetName = titles.find(
    (t) => t.contact_relationship_titles__id === targetId
  )?.contact_relationship_titles__name

  const canConfirm = sourceIds.length > 0 && !!targetId && !sourceIds.includes(targetId)

  return (
    <Modal
      title='Replace title'
      open={open}
      okText='Reassign'
      okButtonProps={{ disabled: !canConfirm }}
      confirmLoading={confirmLoading}
      onOk={() => {
        if (canConfirm && targetId) onConfirm(sourceIds, targetId, deleteSources)
      }}
      onCancel={onCancel}
      destroyOnClose
    >
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        <div>
          <Text strong>Titles to replace</Text>
          <Select
            mode='multiple'
            allowClear
            placeholder='Select one or more titles'
            value={sourceIds}
            options={sourceOptions}
            onChange={(vals) => {
              setSourceIds(vals)
              if (targetId && vals.includes(targetId)) setTargetId(undefined)
            }}
            optionFilterProp='label'
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        <div>
          <Text strong>Replace with</Text>
          <Select
            allowClear
            showSearch
            placeholder='Select the target title (same relationship type)'
            value={targetId}
            options={targetOptions}
            onChange={(val) => setTargetId(val)}
            optionFilterProp='label'
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        <Checkbox
          checked={deleteSources}
          disabled={hasProtectedSource}
          onChange={(e) => setDeleteSources(e.target.checked)}
        >
          Delete the replaced {sourceIds.length === 1 ? 'title' : 'titles'} after reassigning
        </Checkbox>

        {hasProtectedSource && (
          <Alert
            type='warning'
            showIcon
            message='A system-protected title is among the sources — it will be emptied of references but cannot be deleted.'
          />
        )}

        {canConfirm && (
          <Alert
            type='info'
            showIcon
            message={`${affectedReferences} reference(s) — company, deal and prospect contacts — will move to “${targetName}”.`}
          />
        )}
      </Space>
    </Modal>
  )
}
